import * as React from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

// Mirror of RecipientCombobox for the "Receive at" location field. We moved
// off the Radix <Select> here because Radix derives the trigger's displayed
// text from the matching <SelectItem> at the moment its value is set — and on
// async PO load (reopen an existing draft) the items often aren't mounted yet,
// so the trigger fell back to the placeholder and the location looked "lost"
// even though it was in form state. This combobox renders `value` directly as
// the button label, exactly like the vendor picker, so the display never
// depends on item mount timing.

interface Props {
  value: string
  onChange: (value: string) => void
  locations: string[]
  disabled?: boolean
  id?: string
}

export function LocationCombobox({ value, onChange, locations, disabled, id }: Props) {
  const [open, setOpen] = React.useState(false)

  // Always include the current value as an option even if it's not in the
  // static list (legacy POs, or list not loaded yet) so the check mark and
  // search still behave for it.
  const options = React.useMemo(() => {
    if (value && !locations.includes(value)) return [value, ...locations]
    return locations
  }, [value, locations])

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          <span className={cn('truncate', !value && 'text-[var(--mute)]')}>
            {value || 'Select location…'}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command>
          <CommandInput placeholder="Search locations…" />
          <CommandList>
            <CommandEmpty>No location found.</CommandEmpty>
            <CommandGroup>
              {options.map((loc) => (
                <CommandItem
                  key={loc}
                  value={loc}
                  onSelect={() => {
                    onChange(loc)
                    setOpen(false)
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === loc ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {loc}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
